import { Injectable } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { IBaseSiteOpeningHours, SiteOpeningHoursForm } from '../interfaces/customer-sites';
import isEmpty from 'lodash/isEmpty';
import { add, differenceInMinutes, startOfDay, parse } from 'date-fns';
import { DayOfTheWeek } from '../enums/day-of-week';
import { isMidnightMinute } from '../utils/date-utils';
import { HttpClient } from '@angular/common/http';
import { ITaskSiteOpeningHours } from '../interfaces/task-info';

const defaultOpeningHours: IBaseSiteOpeningHours[] = [
  { dayOfWeek: DayOfTheWeek.Monday, open: null, close: null },
  { dayOfWeek: DayOfTheWeek.Tuesday, open: null, close: null },
  { dayOfWeek: DayOfTheWeek.Wednesday, open: null, close: null },
  { dayOfWeek: DayOfTheWeek.Thursday, open: null, close: null },
  { dayOfWeek: DayOfTheWeek.Friday, open: null, close: null },
  { dayOfWeek: DayOfTheWeek.Saturday, open: null, close: null },
  { dayOfWeek: DayOfTheWeek.Sunday, open: null, close: null },
];

export interface IBaseSiteOpeningHoursDisplay {
  open: string;
  close: string;
  dayOfWeek: string;
  isClosed: boolean;
  unspecified: boolean;
}

export interface ITaskSiteOpeningHoursDisplay {
  pickup: IBaseSiteOpeningHoursDisplay[];
  dropOff: IBaseSiteOpeningHoursDisplay[];
}

interface IUpdateTaskSiteOpeningHours {
  pickup: IBaseSiteOpeningHours[];
  dropOff: IBaseSiteOpeningHours[];
}

@Injectable({ providedIn: 'root' })
export class SiteOpeningHoursService {
  constructor(private http: HttpClient) {}

  public updateTaskSiteOpeningHours(
    taskId: number,
    hours: IUpdateTaskSiteOpeningHours,
  ): Promise<ITaskSiteOpeningHoursDisplay> {
    return this.http
      .post<ITaskSiteOpeningHoursDisplay>(`/api/task/${taskId}/site-opening-hours`, hours)
      .toPromise();
  }

  public getTaskSiteOpeningHoursDisplay(taskId: number) {
    return this.http
      .get<ITaskSiteOpeningHoursDisplay>(`/api/task/${taskId}/site-opening-hours-display`)
      .toPromise();
  }

  public createForm(
    requiredDays: DayOfTheWeek[] | null = null,
    requireAtleastOneDayOpen: boolean = false,
  ): FormArray<FormGroup<SiteOpeningHoursForm>> {
    if (typeof requiredDays == 'undefined' || requiredDays == null) {
      requiredDays = defaultOpeningHours.map((x) => x.dayOfWeek);
    }

    return new FormArray<FormGroup<SiteOpeningHoursForm>>(
      defaultOpeningHours.map(
        (x) =>
          new FormGroup<SiteOpeningHoursForm>({
            open: new FormControl<Date>(null, getValidators(requiredDays, x.dayOfWeek)),
            close: new FormControl<Date>(null, [
              ...getValidators(requiredDays, x.dayOfWeek),
              closeGreaterThanOpenValidator,
            ]),
            dayOfWeek: new FormControl<number>(x.dayOfWeek),
          }),
      ),
      requireAtleastOneDayOpen ? requireAtLeastOneDayOpenValidator : null,
    );
  }

  public patchForm(
    form: FormArray<FormGroup<SiteOpeningHoursForm>>,
    openingHours: IBaseSiteOpeningHours[],
  ): void {
    let startOfToday = startOfDay(new Date());
    form.patchValue(
      openingHours.map((x) => ({
        close: x.close != null ? add(startOfToday, { minutes: x.close }) : null,
        open: x.open != null ? add(startOfToday, { minutes: x.open }) : null,
        dayOfWeek: x.dayOfWeek,
      })),
    );
  }

  public patchFormFromDisplay(
    form: FormArray<FormGroup<SiteOpeningHoursForm>>,
    openingHours: IBaseSiteOpeningHoursDisplay[],
  ): void {
    form.patchValue(
      openingHours.map((x) => ({
        close: x.close != null ? parse(x.close, 'HH:mm', new Date()) : null,
        open: x.open != null ? parse(x.open, 'HH:mm', new Date()) : null,
        dayOfWeek: DayOfTheWeek[x.dayOfWeek],
      })),
    );
  }

  public resetForm(form: FormArray<FormGroup<SiteOpeningHoursForm>>): void {
    let startOfToday = startOfDay(new Date());
    form.patchValue(
      defaultOpeningHours.map((x) => ({
        close: x.close != null ? add(startOfToday, { minutes: x.close }) : null,
        open: x.open != null ? add(startOfToday, { minutes: x.open }) : null,
        dayOfWeek: x.dayOfWeek,
      })),
    );
  }

  public getOpeningHours(
    form: FormArray<FormGroup<SiteOpeningHoursForm>>,
  ): IBaseSiteOpeningHours[] {
    let startOfToday = startOfDay(new Date());
    return form.getRawValue().map((hours) => ({
      open: differenceInMinutes(hours.open, startOfToday),
      close: differenceInMinutes(hours.close, startOfToday),
      dayOfWeek: hours.dayOfWeek,
    }));
  }
}

const getValidators = (requiredDays: DayOfTheWeek[], dayOfWeek: DayOfTheWeek) => {
  return requiredDays.includes(dayOfWeek) ? [Validators.required] : [];
};

const requireAtLeastOneDayOpenValidator: ValidatorFn = (
  formArray: FormArray<FormGroup<SiteOpeningHoursForm>>,
): ValidationErrors | null => {
  const allDays = formArray.getRawValue();

  const closedDays = allDays.filter((x) => isMidnightMinute(x.open) && isMidnightMinute(x.close));

  if (closedDays.length == allDays.length) {
    return { requireAtLeastOneDayOpen: true };
  }

  return null;
};

const closeGreaterThanOpenValidator: ValidatorFn = (
  control: FormControl<Date>,
): ValidationErrors | null => {
  const parent = control.parent as FormGroup<SiteOpeningHoursForm>;

  if (!parent) {
    return null;
  }

  const closeTimeControl = control;
  const openTimeControl = parent.controls.open;

  const closeTimeValue =
    closeTimeControl.value == null
      ? null
      : differenceInMinutes(closeTimeControl.value, startOfDay(closeTimeControl.value));

  const openTimeValue =
    openTimeControl.value == null
      ? null
      : differenceInMinutes(openTimeControl.value, startOfDay(openTimeControl.value));

  if (openTimeValue == null || closeTimeValue == null) {
    return null;
  }

  if (closeTimeValue == 0 && openTimeValue == 0) {
    return null;
  }

  if (closeTimeValue <= openTimeValue) {
    return { closeGreaterThanOpen: true };
  }

  return null;
};
